import React from 'react'
import { DollarSign } from 'lucide-react'
import { StatsCard } from './stats/StatsCard'
import { formatMoney } from '@/utils'
import type { MonthlyAvgDataPoint } from '@/types/otc'
import { buildChartSeries } from './charts/otcSeries'

interface AverageAmountCardProps {
  averageAmount: number
  loading?: boolean
  monthlyAvg?: MonthlyAvgDataPoint[]
  label?: string
}

export interface AverageAmountBarData {
  value: number
}

const AverageAmountChart: React.FC<{ monthlyAvg?: MonthlyAvgDataPoint[]; seed: number }> = ({ monthlyAvg, seed }) => {
  const heights = buildChartSeries({
    source: monthlyAvg,
    count: 15,
    min: 13,
    max: 100,
    seed,
  })
  const averageHeight = Math.round(heights.reduce((sum, value) => sum + value, 0) / heights.length)
  const avgLineTop = Math.max(16, Math.min(88, 100 - averageHeight))
  const avgLabelTop = Math.max(0, avgLineTop - 11)

  return (
    <div className="relative h-full w-full max-w-[214px]">
      <div
        className="absolute left-0 h-px w-full border-t border-dotted border-[rgba(45,41,35,0.32)] dark:border-[rgba(253,253,253,0.32)]"
        style={{ top: `${avgLineTop}px` }}
      />

      <div className="absolute left-0 flex items-start" style={{ top: `${avgLabelTop}px` }}>
        <div className="flex h-5 items-center bg-[#313131] dark:bg-white pl-1 pr-0.5 text-[13px] font-medium leading-[18px] text-white dark:text-[#0d0d0d]">
          Avg
        </div>
        <div className="h-5 w-2.5 border-y-[10px] border-l-[10px] border-y-transparent border-l-[#313131] dark:border-l-white" />
      </div>

      <div className="absolute bottom-0 left-0 flex h-[100px] w-full items-end gap-[6px]">
        {heights.map((height, index) => (
          <div
            key={`bar-${index}`}
            className="flex-1 rounded-t-[12px] bg-gradient-to-b from-[rgba(197,204,159,0.52)] to-[rgba(197,204,159,0.2)]"
            style={{ height: `${height}px` }}
          />
        ))}
      </div>
    </div>
  )
}

export const AverageAmountCard: React.FC<AverageAmountCardProps> = ({
  averageAmount,
  loading = false,
  monthlyAvg,
  label = 'Average Amount',
}) => {
  return (
    <StatsCard
      icon={<DollarSign className="h-4 w-4" strokeWidth={1.8} />}
      iconAlt="Average Amount"
      label={label}
      value={
        <>
          <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>
          <span className="text-[#2d2923] dark:text-[#fdfdfd]">{formatMoney(averageAmount).replace('$', '').trim()}</span>
        </>
      }
      loading={loading}
      mobileHeight="h-[126px]"
      chart={<AverageAmountChart monthlyAvg={monthlyAvg} seed={averageAmount} />}
    />
  )
}
